import React, { useState, useEffect } from 'react';
import { buildApiUrl } from '../config/api';

interface GameLoaderProps {
  onComplete: () => void;
}

const DEFAULT_MESSAGES = [
  'Bonjour !',
  'Bienvenue chez Phoelynx France !',
  'Préparation de votre expérience...',
];

// Tailles de bulle configurables depuis l'admin (réglage loaderBubbleSize).
const BUBBLE_SIZES: Record<string, string> = {
  sm: 'max-w-[16rem] px-4 py-3 text-base',
  md: 'max-w-xs px-6 py-4 text-lg',
  lg: 'max-w-md px-8 py-6 text-2xl',
};

const GameLoader: React.FC<GameLoaderProps> = ({ onComplete }) => {
  const [currentMessage, setCurrentMessage] = useState(0);
  const [showBubble, setShowBubble] = useState(false);
  const [logoScale, setLogoScale] = useState(0.5);
  const [logoRotation, setLogoRotation] = useState(0);
  const [messages, setMessages] = useState<string[]>(DEFAULT_MESSAGES);
  const [bubbleSize, setBubbleSize] = useState('md');

  // Messages et taille de bulle personnalisables (réglages publics). Chargés
  // en tâche de fond ; les valeurs par défaut s'affichent en attendant.
  useEffect(() => {
    fetch(buildApiUrl('/settings/public'))
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (!data) return;
        const rawMsgs = data.loaderMessages?.value;
        try {
          const parsed = typeof rawMsgs === 'string' ? JSON.parse(rawMsgs) : rawMsgs;
          if (Array.isArray(parsed) && parsed.length > 0) setMessages(parsed.map(String).filter(Boolean));
        } catch { /* on garde les messages par défaut */ }
        const size = data.loaderBubbleSize?.value;
        if (typeof size === 'string' && BUBBLE_SIZES[size]) setBubbleSize(size);
      })
      .catch(() => { /* réglages indisponibles : valeurs par défaut */ });
  }, []);

  useEffect(() => {
    // Animation d'entrée du logo
    const logoAnimation = setTimeout(() => {
      setLogoScale(1);
      setLogoRotation(360);
    }, 200);

    // Séquence : bulle affichée, puis défilement des messages sur une fenêtre
    // fixe (~6 s), quel que soit leur nombre, puis fin.
    const WINDOW_MS = 5200;
    const startBubble = setTimeout(() => setShowBubble(true), 800);
    const step = messages.length > 1 ? WINDOW_MS / (messages.length - 1) : WINDOW_MS;
    const messageTimers = messages.map((_, i) =>
      i === 0 ? null : setTimeout(() => setCurrentMessage(i), 800 + step * i)
    );
    const hideBubble = setTimeout(() => setShowBubble(false), 800 + WINDOW_MS);
    const complete = setTimeout(onComplete, 800 + WINDOW_MS + 500);

    return () => {
      clearTimeout(logoAnimation);
      clearTimeout(startBubble);
      messageTimers.forEach((t) => t && clearTimeout(t));
      clearTimeout(hideBubble);
      clearTimeout(complete);
    };
  }, [onComplete, messages]);

  return (
    <div className="fixed inset-0 z-50 bg-gradient-to-br from-blue-900 via-gray-800 to-red-900 flex items-center justify-center overflow-hidden">
      {/* Animated background particles */}
      <div className="absolute inset-0">
        {[...Array(20)].map((_, i) => (
          <div
            key={i}
            className="absolute w-2 h-2 bg-blue-400/30 rounded-full animate-pulse"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              animationDelay: `${Math.random() * 2}s`,
              animationDuration: `${2 + Math.random() * 3}s`
            }}
          />
        ))}
      </div>

      {/* Main character container */}
      <div className="relative flex flex-col items-center">
        {/* Speech bubble */}
        {showBubble && (
          <div className="mb-8 animate-bounce">
            <div className={`relative bg-white/95 backdrop-blur-sm rounded-2xl shadow-2xl border-2 border-blue-300/50 ${BUBBLE_SIZES[bubbleSize] ?? BUBBLE_SIZES.md}`}>
              <div className="text-center">
                <p className="text-gray-800 font-semibold">
                  {messages[Math.min(currentMessage, messages.length - 1)]}
                </p>
              </div>
              {/* Bubble pointer */}
              <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 translate-y-full">
                <div className="w-4 h-4 bg-white/95 rotate-45 border-r-2 border-b-2 border-blue-300/50"></div>
              </div>

              {/* Sparkle effects */}
              <div className="absolute -top-2 -right-2 w-3 h-3 bg-yellow-400 rounded-full animate-ping"></div>
              <div className="absolute -top-1 -left-3 w-2 h-2 bg-blue-400 rounded-full animate-pulse"></div>
            </div>
          </div>
        )}

        {/* Character (Phoelynx logo) */}
        <div className="relative">
          <img
            src="/assets/logos/Phoelynx_Transparent.webp"
            alt="Phoelynx"
            className="w-48 h-48 object-contain filter drop-shadow-2xl transition-all duration-1000 ease-out"
            style={{
              transform: `scale(${logoScale}) rotate(${logoRotation}deg)`,
            }}
          />

          {/* Glow effect */}
          <div className="absolute inset-0 w-32 h-32 bg-gradient-to-r from-blue-400/30 to-red-400/30 rounded-full blur-xl animate-pulse"></div>

          {/* Orbiting elements */}
          <div className="absolute inset-0 w-32 h-32">
            <div className="absolute w-2 h-2 bg-yellow-400 rounded-full animate-spin" style={{
              top: '10%',
              right: '10%',
              animationDuration: '3s'
            }}></div>
            <div className="absolute w-1.5 h-1.5 bg-blue-400 rounded-full animate-spin" style={{
              bottom: '10%',
              left: '10%',
              animationDuration: '4s',
              animationDirection: 'reverse'
            }}></div>
            <div className="absolute w-2 h-2 bg-red-400 rounded-full animate-spin" style={{
              top: '50%',
              left: '-5%',
              animationDuration: '5s'
            }}></div>
          </div>
        </div>

        {/* Loading dots */}
        <div className="flex space-x-2 mt-8">
          {[0, 1, 2].map((i) => (
            <div
              key={i}
              className="w-3 h-3 bg-gradient-to-r from-blue-400 to-red-400 rounded-full animate-bounce"
              style={{ animationDelay: `${i * 0.2}s` }}
            />
          ))}
        </div>

        {/* Loading text */}
        <p className="text-white/80 text-sm mt-4 animate-pulse">
          Chargement...
        </p>
      </div>

      {/* Corner decorations */}
      <div className="absolute top-8 left-8 w-16 h-16 border-l-2 border-t-2 border-blue-400/30 animate-pulse"></div>
      <div className="absolute top-8 right-8 w-16 h-16 border-r-2 border-t-2 border-red-400/30 animate-pulse"></div>
      <div className="absolute bottom-8 left-8 w-16 h-16 border-l-2 border-b-2 border-red-400/30 animate-pulse"></div>
      <div className="absolute bottom-8 right-8 w-16 h-16 border-r-2 border-b-2 border-blue-400/30 animate-pulse"></div>
    </div>
  );
};

export default GameLoader;